import React, { useMemo } from "react";

import { useChartData, UseChartDataParams } from "@/api/useChartData";

import { ContactPointCard } from ".";

/**
 * Subset of ChartsData type containing only the fields relevant for contact point top charts.
 * Properly types the contact point data arrays to avoid casting and improve type safety.
 */
type ChartDataset = {
  points_contact?: number[][] | null;
  is_hit?: boolean;
  is_out?: boolean;
};

/**
 * Props for the ContactPointDataCard component.
 *
 * This interface extends the base chart data parameters to provide
 * contact point chart specific configuration.
 */
export interface ContactPointDataCardProps extends UseChartDataParams {
  /** The title to display on the contact point chart card */
  title: string;
}

/**
 * ContactPointDataCard Component
 *
 * A data-driven contact point visualization component that fetches real player data
 * and displays it as a top-down view of where the ball made contact with the bat.
 *
 * Features:
 * - Fetches live player chart data from the API
 * - Transforms backend data into contact point coordinate format
 * - Shows loading states and error handling
 * - Supports various filters (result type, pitch type, etc.)
 *
 * @param props - Configuration for the contact point chart including data parameters and display options
 * @returns A rendered contact point chart card with player data
 *
 * @example
 * ```tsx
 * <ContactPointDataCard
 *   title="Point of Contact - Top"
 *   playerExternalId="player-123"
 *   startDate="2024-01-01"
 *   endDate="2024-12-31"
 *   gameType="game"
 *   playerType="hitter"
 *   vsHandedness="all"
 *   chartType="points_contact"
 * />
 * ```
 */
export const ContactPointDataCard: React.FC<ContactPointDataCardProps> = ({
  title,
  playerExternalId,
  startDate,
  endDate,
  gameType,
  playerType,
  vsHandedness,
  chartType,
  resultType,
  pitchType,
  filters,
  groupBy,
}) => {
  const { data, isLoading, error } = useChartData({
    playerExternalId,
    startDate,
    endDate,
    gameType,
    playerType,
    vsHandedness,
    chartType,
    resultType,
    pitchType,
    filters,
    groupBy,
    hitsAndOutsOnly: true,
  });

  const contactData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    // Collect all coordinates with their hit/out status
    const allCoordinates: Array<{
      xFeet: number;
      yFeet: number;
      zFeet: number;
      result?: string;
    }> = [];

    data.forEach((rawDataset) => {
      const dataset = rawDataset as ChartDataset;
      const contactTopData = dataset.points_contact;

      if (contactTopData && Array.isArray(contactTopData)) {
        contactTopData.forEach((coords: number[]) => {
          let result = "contact";
          if (dataset.is_hit) {
            result = "Hits";
          } else if (dataset.is_out) {
            result = "Outs";
          }

          allCoordinates.push({
            xFeet: coords[0],
            yFeet: coords[1],
            zFeet: coords[2],
            result,
          });
        });
      }
    });

    return [
      {
        coordinates: allCoordinates,
      },
    ];
  }, [data]);

  if (error) {
    return (
      <div className="w-full min-h-[330px] h-full flex items-center justify-center bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-500">Unable to load chart data</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <ContactPointCard title={title} data={[]}>
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
          <div className="text-sm text-gray-500">Loading...</div>
        </div>
      </ContactPointCard>
    );
  }

  return <ContactPointCard title={title} data={contactData} />;
};
